import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X, ShieldAlert, ArrowRight, IdCard, Lock } from "lucide-react";

/**
 * IdentityGateModal — global reaction to axios 403/identity_required events.
 * Pops when a user without identity verification tries to post, DM, comment,
 * or apply to a job. Sends them to /verify/identity.
 */
export default function IdentityGateModal() {
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    const handler = (e) => setDetail(e.detail || {});
    window.addEventListener("pjp:identity-required", handler);
    return () => window.removeEventListener("pjp:identity-required", handler);
  }, []);

  if (!detail) return null;

  const feature = detail.feature || "This action";
  const verifyUrl = detail.verify_url || "/verify/identity";
  const close = () => setDetail(null);

  return (
    <div
      role="dialog"
      aria-modal="true"
      data-testid="identity-gate-modal"
      className="fixed inset-0 z-[9999] flex items-center justify-center px-4 bg-black/70 backdrop-blur-md animate-in fade-in duration-200"
      onClick={close}
    >
      <div
        className="relative w-full max-w-md rounded-2xl border border-orange-hud/40 bg-obsidian/95 p-7 overflow-hidden shadow-[0_0_60px_-10px_rgba(255,138,61,0.5)]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(circle at 100% 0%, rgba(255,138,61,0.20), transparent 55%), radial-gradient(circle at 0% 100%, rgba(75,156,255,0.10), transparent 55%)" }} />
        <span className="hud-c-tl" /><span className="hud-c-tr" />
        <span className="hud-c-bl" /><span className="hud-c-br" />

        <button
          type="button"
          onClick={close}
          data-testid="identity-gate-close"
          className="absolute top-3 right-3 h-8 w-8 rounded-full border border-white/10 flex items-center justify-center text-white/60 hover:text-white hover:border-white/30 transition-colors z-10"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="relative">
          <div className="flex items-center gap-2 mb-3">
            <ShieldAlert className="h-4 w-4 text-orange-hud" />
            <div className="mono text-[10px] tracking-[0.28em] uppercase text-orange-hud">// IDENTITY · REQUIRED</div>
          </div>
          <h2 className="font-display text-2xl md:text-3xl font-bold tracking-tighter leading-tight mb-2">
            First — prove you're you.
          </h2>
          <p className="text-white/70 text-[14px] leading-relaxed mb-5">
            <span className="text-white font-semibold">{feature}</span> is restricted to identity-verified members.
            PJP is a real-name community — we protect our crew from fake accounts by requiring a government
            photo ID at the door.
          </p>

          <div className="grid grid-cols-2 gap-2 mb-6">
            {[
              { icon: IdCard, label: "Passport or Driver's License" },
              { icon: Lock,   label: "Encrypted · never shared" },
            ].map(({ icon: Ico, label }) => (
              <div key={label} className="flex items-center gap-1.5 text-[12px] text-white/70">
                <Ico className="h-3.5 w-3.5 text-cyan flex-shrink-0" />
                <span>{label}</span>
              </div>
            ))}
          </div>
          <div className="text-[12px] text-white/55 mb-6">
            Takes about 30 seconds. Auto-verified instantly if the name matches your account.
          </div>

          <Link
            to={verifyUrl}
            data-testid="identity-gate-cta"
            onClick={close}
            className="w-full inline-flex items-center justify-center gap-2 rounded-full px-5 py-3 bg-orange-hud text-obsidian font-display font-semibold tracking-wide shadow-[0_0_20px_-4px_rgba(255,138,61,0.7)] hover:brightness-105 transition-all"
          >
            <IdCard className="h-4 w-4" /> VERIFY MY IDENTITY <ArrowRight className="h-4 w-4" />
          </Link>

          <button
            type="button"
            onClick={close}
            data-testid="identity-gate-later"
            className="mt-3 w-full text-[12px] text-white/50 hover:text-white transition-colors"
          >
            Not right now
          </button>
        </div>
      </div>
    </div>
  );
}
